/**
 * PipelineSteps - visualizes the stages of a QR receipt processing pipeline.
 * Accepts a `logs` array (e.g. ["fetch", "preprocess", "selector", "done"]) and
 * an optional `strategy` string indicating which parser was ultimately chosen.
 *
 * Steps are shown left-to-right with colour-coded status badges.
 */

// All known pipeline stages in order of execution
const PIPELINE_STAGES = ['fetch', 'preprocess', 'heuristic', 'selector', 'llm', 'validation']

const STAGE_COLORS = {
  fetch:       'bg-blue-100 text-blue-700',
  preprocess:  'bg-indigo-100 text-indigo-700',
  heuristic:   'bg-yellow-100 text-yellow-700',
  selector:    'bg-violet-100 text-violet-700',
  llm:         'bg-pink-100 text-pink-700',
  validation:  'bg-emerald-100 text-emerald-700',
}

const DEFAULT_COLOR = 'bg-surface-elevated text-muted-foreground'

export default function PipelineSteps({ logs = [], strategy = null }) {
  if (!logs || logs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">No pipeline log available.</p>
    )
  }

  // Normalise log entries to lowercase for matching
  const executedStages = new Set(logs.map((l) => String(l).toLowerCase()))

  // Determine all unique stage names: known stages in order + any extra from logs
  const extraStages = logs.filter((l) => !PIPELINE_STAGES.includes(String(l).toLowerCase()))
  const allStages = [
    ...PIPELINE_STAGES.filter((s) => executedStages.has(s)),
    ...extraStages.filter((s, i, arr) => arr.indexOf(s) === i),
  ]

  return (
    <div className="space-y-3">
      {/* step badges */}
      <div className="flex flex-wrap gap-2">
        {logs.map((step, idx) => {
          const key = String(step).toLowerCase()
          const colorClass = STAGE_COLORS[key] ?? DEFAULT_COLOR
          const isStrategy = strategy && key === strategy.toLowerCase()
          return (
            <span
              key={idx}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${colorClass} ${isStrategy ? 'ring-2 ring-offset-1 ring-current' : ''}`}
            >
              {idx + 1}. {step}
              {isStrategy && <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">chosen</span>}
            </span>
          )
        })}
      </div>

      {strategy && (
        <p className="text-sm text-muted-foreground">
          Strategy used: <span className="font-semibold text-violet-700">{strategy}</span>
        </p>
      )}
    </div>
  )
}

